import { Menu } from 'components/Menu/Menu'
import { Section } from '@react-stately/collections'
import {
  AlignLeft,
  ArrowRotateLeft2,
  CheckList,
  Edit3,
  Font,
  FontWeight,
  LetterSpacing3,
  List,
  ListNumbers,
  Title,
  ToggleBlock,
  Type3,
  UserProfile,
} from 'blaze-ui/icons'
import { MenuProps } from 'components/Menu/Types'
import { Key, useMemo } from 'react'
import { TEXT_STYLES } from 'common/Prose/constants'
import { useAlmanacEditorState } from 'stores/EditorState'
import { observer } from 'mobx-react-lite'
import { Text } from 'blaze-ui/Text/Text'
import { TextColorIcon } from 'common/Prose/Toolbar/TextStylesMenu/TextColorIcon'
import { HighlightColorIcon } from 'common/Prose/Toolbar/TextStylesMenu/HighlightColorIcon'
import { FONT_WEIGHTS } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/constants'
import { FontFamilyType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { useToggleBlockToggle } from 'common/Prose/Toolbar/hooks/useToggleBlockToggle'
import { useBulletListToggle } from 'common/Prose/Toolbar/hooks/useBulletListToggle'
import { useNumberedListToggle } from 'common/Prose/Toolbar/hooks/useNumberedListToggle'
import { useCheckListToggle } from 'common/Prose/Toolbar/hooks/useCheckListToggle'
import { ArticleService } from 'api/services/ArticleService'
import {
  DefaultArticleStyle,
  TextStyleModal,
} from 'common/Prose/Toolbar/components/Format/components/MainStylePane/components/TextBlock/components/TextStyleModal/TextStyleModal'
import { handleResponseWithMessage } from 'api/utils/handleResponseWithMessage'
import { useDocStore } from 'stores/DocStore'
import { useModals } from 'almanac-ui/Modal'
import { ItemElement } from '@react-types/shared'
import { Item } from 'components/Menu/MenuItem'
import { ChevronRightSmall } from 'blaze-ui/icons'

export const TextStylesMenu = observer(
  ({
    onAction,
    menuType = 'dropdown',
    hiddenKeys = new Set(),
  }: Pick<MenuProps<any>, 'menuType' | 'onAction'> & { hiddenKeys?: Set<Key> }) => {
    const view = useEditorView()
    const docStore = useDocStore()
    const { openModal } = useModals()

    const {
      active: isToggleBlockActive,
      disabled: isToggleBlockDisabled,
      handleClick: handleToggleBlock,
    } = useToggleBlockToggle(view)
    const {
      active: isBulletListActive,
      handleClick: handleBulletList,
      disabled: isBulletListDisabled,
    } = useBulletListToggle(view)
    const {
      active: isNumberedListActive,
      disabled: isNumberedListDisabled,
      handleClick: handleNumberedList,
    } = useNumberedListToggle(view)
    const {
      active: isCheckListActive,
      handleClick: handleCheckList,
      disabled: isCheckListDisabled,
    } = useCheckListToggle(view)

    const editorState = useAlmanacEditorState()
    const defaultState = {
      activeFontSizes: new Set<number>(),
      activeFamilies: new Set<FontFamilyType>(),
      activeStyles: new Set<keyof typeof TEXT_STYLES>(),
      activeWeights: new Set<keyof typeof FONT_WEIGHTS>(),
      activeAlignments: new Set<string>(),
    }

    const { activeStyles, activeWeights, activeFamilies, activeFontSizes, activeAlignments } =
      editorState || defaultState

    const resetToDefaultStyles = async () => {
      await ArticleService.updateArticle(docStore.articleId, { article_style_attributes: DefaultArticleStyle }).then(
        handleResponseWithMessage({ successMessage: 'Doc styles reset to default' }),
      )
    }

    const handleAction = async (action: Key) => {
      onAction?.(action)
      switch (action) {
        case 'textStyle':
          // handle in parent
          return
        case 'fontFamily':
          // handle in parent
          return
        case 'fontWeight':
          // handle in parent
          return
        case 'fontSize':
          // handle in parent
          return
        case 'alignment':
          // handle in parent
          return
        case 'textColor':
          // handle in parent
          return
        case 'textHighlight':
          // handle in parent
          return
        case 'callout':
          // handle in parent
          return
        case 'quote':
          // handle in parent
          return
        case 'numberedList':
          return handleNumberedList()
        case 'bulletedList':
          return handleBulletList()
        case 'checkList':
          return handleCheckList()
        case 'toggleList':
          return handleToggleBlock()
        case 'resetStyles':
          return resetToDefaultStyles()
        case 'editStyles':
          return openModal(TextStyleModal)
      }
    }

    const activeTextStyle = Array.from(activeStyles).pop()
    const activeWeight: keyof typeof FONT_WEIGHTS | undefined = Array.from(activeWeights).pop()
    const activeFamily: FontFamilyType | undefined = Array.from(activeFamilies).pop()
    const activeFontSize: number | undefined = Array.from(activeFontSizes).pop()
    const activeAlignment: string | undefined = Array.from(activeAlignments).pop()

    const selectedKeys = useMemo(() => {
      const set = new Set<Key>()
      isToggleBlockActive && set.add('toggleList')
      isBulletListActive && set.add('bulletedList')
      isNumberedListActive && set.add('numberedList')
      isCheckListActive && set.add('checkList')
      return set
    }, [isToggleBlockActive, isBulletListActive, isNumberedListActive, isCheckListActive])

    const disabledKeys = useMemo(() => {
      const set = new Set<Key>()
      isToggleBlockDisabled && set.add('toggleList')
      isBulletListDisabled && set.add('bulletedList')
      isNumberedListDisabled && set.add('numberedList')
      isCheckListDisabled && set.add('checkList')
      return set
    }, [isToggleBlockDisabled, isBulletListDisabled, isNumberedListDisabled, isCheckListDisabled])

    const Chevron = menuType === 'tray' ? ChevronRightSmall : undefined

    return (
      <Menu
        menuType={menuType}
        onAction={handleAction}
        selectedKeys={selectedKeys}
        disabledKeys={disabledKeys}
        selectionMode="multiple"
      >
        <Section>
          <Item key="textStyle" textValue="Text Style" frontIcon={Title} endIcon={Chevron}>
            Text Style
            {activeTextStyle && <Text variant="secondary">{TEXT_STYLES[activeTextStyle].textValue}</Text>}
          </Item>
          <Item key="fontFamily" textValue="Font Family" frontIcon={Font} endIcon={Chevron}>
            Font
            {activeFamily && <Text variant="secondary">{activeFamily}</Text>}
          </Item>
          <Item key="fontWeight" textValue="Font Weight" frontIcon={FontWeight} endIcon={Chevron}>
            Weight
            {activeWeight && <Text variant="secondary">{FONT_WEIGHTS[activeWeight]}</Text>}
          </Item>
          <Item key="fontSize" textValue="Font Size" frontIcon={LetterSpacing3} endIcon={Chevron}>
            Size
            {activeFontSize && <Text variant="secondary">{activeFontSize}</Text>}
          </Item>
          <Item key="alignment" textValue="Alignment" frontIcon={AlignLeft} endIcon={Chevron}>
            Alignment
            {activeAlignment && <Text variant="secondary">{activeAlignment}</Text>}
          </Item>
        </Section>
        <Section>
          <Item key="numberedList" textValue="Numbered List" frontIcon={ListNumbers}>
            Numbered List
          </Item>
          <Item key="bulletedList" textValue="Bulleted List" frontIcon={List}>
            Bulleted List
          </Item>
          <Item key="checkList" textValue="Check List" frontIcon={CheckList}>
            Check List
          </Item>
          <Item key="toggleList" textValue="Toggle List" frontIcon={ToggleBlock}>
            Toggle List
          </Item>
        </Section>
        <Section>
          <Item key="textColor" textValue="Text Color" frontIcon={TextColorIcon} endIcon={Chevron}>
            Color
          </Item>
          <Item key="textHighlight" textValue="Text Highlight" frontIcon={HighlightColorIcon} endIcon={Chevron}>
            Highlight
          </Item>
          <Item key="callout" textValue="Callout" frontIcon={Type3} endIcon={Chevron}>
            Callout
          </Item>
          <Item key="quote" textValue="Quote" frontIcon={UserProfile} endIcon={Chevron}>
            Quote
          </Item>
        </Section>
        <Section>
          {
            [
              !hiddenKeys.has('editStyles') && (
                <Item key="editStyles" textValue="Edit All Styles" frontIcon={Edit3} endIcon={Chevron}>
                  Edit All Text Styles
                </Item>
              ),
              !hiddenKeys.has('resetStyles') && (
                <Item
                  key="resetStyles"
                  textValue="Reste to Default Styles"
                  frontIcon={ArrowRotateLeft2}
                  endIcon={Chevron}
                >
                  Rest to Default Styles
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
      </Menu>
    )
  },
)
